/**
 * Smokey's Cart Delivery System v2
 * Handles delivery windows, express delivery, and live ETA
 * 
 * Fixes applied:
 * - Per-function rate limiting
 * - Proper cart cache management
 * - Missing express.updateVisibility
 * - Interval cleanup on unload
 * - Cooldown check before rate limiter
 * - Edge case fixes
 */

(function() {
  'use strict';

  // Namespace everything under SmokeysCart
  window.SmokeysCart = window.SmokeysCart || {};
  
  // Prevent duplicate initialization
  if (window.SmokeysCart.initialized) {
    // Already initialized, skipping
    return;
  }

  // Selector constants
  const SEL = {
    liveBadge: '.live-badge',
    etaPill: '.eta-pill',
    etaValue: '#eta-value',
    expressOpt: '#express-delivery-option',
    expressCheckbox: '#express-delivery-checkbox',
    expressCard: '.express-card',
    overlay: '.express-unavailable-overlay',
    stickyBanner: '.sc-express-sticky'
  };

  // Configuration
  const CONFIG = {
    DAYS_TO_SHOW: 3,
    API_RATE_LIMIT_MS: 5000, // 5 seconds between Distance Matrix calls
    CART_CACHE_TTL: 30000, // 30 seconds
    ETA_COOLDOWN_HOURS: 1, // Cooldown after API quota error
    FREE_DELIVERY_THRESHOLD: 4999, // cents
    // Driver-centric constants
    BULK_PICKUP_MIN: 10,     // driver must be at store T-10
    PACK_MIN: 4,             // pack/hand-off time for Express
    RETURN_BUFFER_MIN: 10,   // driver back at store before bulk load
    SAFETY_PADDING: 1.25     // 25% buffer for safety
  };

  // Debug mode - set to true in preview themes
  const DEBUG = window.__DEV__ || window.location.search.includes('preview_theme_id');
  
  // Safe console wrapper
  const logError = DEBUG ? console.error : () => {};

  // State management
  const state = {
    cartCache: null,
    cartCacheTime: 0,
    observers: [],
    intervals: [],
    initialized: false
  };

  // Helper utilities
  const utils = {
    qs: (selector) => document.querySelector(selector),
    qsa: (selector) => document.querySelectorAll(selector),
    
    // Event bus for clean communication
    eventBus: (() => {
      const bus = document.createElement('div');
      return {
        on: (event, callback) => bus.addEventListener(event, callback),
        off: (event, callback) => bus.removeEventListener(event, callback),
        emit: (event, detail) => bus.dispatchEvent(new CustomEvent(event, { detail }))
      };
    })(),

    // Rate limiter for API calls (per-function)
    rateLimiter: (fn, delay = CONFIG.API_RATE_LIMIT_MS) => {
      let lastCall = 0;
      return (...args) => {
        const now = Date.now();
        if (now - lastCall > delay) {
          lastCall = now;
          return fn(...args);
        }
        // Rate limited, skipping API call
        return Promise.resolve(null);
      };
    },

    // Cached cart fetching
    getCart: async (force = false) => {
      const now = Date.now();
      if (!force && state.cartCache && (now - state.cartCacheTime < CONFIG.CART_CACHE_TTL)) {
        return state.cartCache;
      }
      
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          throw new Error(`Cart fetch failed: ${response.status}`);
        }
        const data = await response.json();
        // Only update cache after successful fetch
        state.cartCache = data;
        state.cartCacheTime = now;
        utils.eventBus.emit('cart:updated', state.cartCache);
        return state.cartCache;
      } catch (error) {
        logError('[SmokeysCart] Error fetching cart:', error);
        return state.cartCache; // Return stale cache if available
      }
    },

    // Haversine distance calculation
    haversineKm: (lat1, lon1, lat2, lon2) => {
      const R = 6371;
      const toRad = d => d * Math.PI / 180;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    // Parse position safely
    parsePosition: (positionStr) => {
      try {
        return JSON.parse(positionStr);
      } catch (e) {
        logError('[SmokeysCart] Invalid position data:', e);
        return null;
      }
    },

    // Clean up resources
    cleanup: () => {
      state.observers.forEach(obs => obs.disconnect());
      state.intervals.forEach(id => clearInterval(id));
      state.observers = [];
      state.intervals = [];
    }
  };

  // Google Maps integration
  const maps = {
    ready: new Promise(resolve => {
      window.SmokeysCart.onGoogleReady = () => {
        // Google Maps ready
        resolve();
      };
    }),

    // Rate-limited fetch with retry logic and caching
    fetchLiveEtaImpl: async (userPos, storePos) => {
      const normalize = p => ({
        lat: +('latitude' in p ? p.latitude : p.lat),
        lng: +('longitude' in p ? p.longitude : p.lng)
      });
      const origin = normalize(userPos);
      const dest = normalize(storePos);

      // Quick param sanity check
      if ([origin.lat, origin.lng, dest.lat, dest.lng].some(n => Number.isNaN(n))) {
        return Promise.reject(new Error('INVALID_LAT_LNG'));
      }

      // Simple in-memory cache keyed by origin→dest pair
      const cacheKey = `${origin.lat},${origin.lng}-${dest.lat},${dest.lng}`;
      if (maps._etaCache?.[cacheKey] && Date.now() - maps._etaCache[cacheKey].ts < 120000) {
        // Using cached ETA
        return maps._etaCache[cacheKey].value;
      }

      // Check cooldown before making request
      const cooldownUntil = sessionStorage.getItem('eta_cooldown_until');
      if (cooldownUntil && Date.now() < parseInt(cooldownUntil)) {
        // API in cooldown period
        return null;
      }

      await maps.ready;
      
      // Retry loop
      let attempt = 0;
      const maxAttempts = 3;

      const run = (delay = 0) => new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
            if (!google.maps.DistanceMatrixService) {
              throw new Error('Google Maps DistanceMatrixService not available');
            }
            
            const svc = new google.maps.DistanceMatrixService();
            svc.getDistanceMatrix({
              origins: [new google.maps.LatLng(origin.lat, origin.lng)],
              destinations: [new google.maps.LatLng(dest.lat, dest.lng)],
              travelMode: google.maps.TravelMode.DRIVING,
              drivingOptions: {
                departureTime: new Date(),
                trafficModel: google.maps.TrafficModel.BEST_GUESS
              },
              unitSystem: google.maps.UnitSystem.METRIC
            }).then(res => {
              const el = res.rows?.[0]?.elements?.[0];
              if (el?.status === 'OK') {
                const seconds = el.duration_in_traffic ? el.duration_in_traffic.value : el.duration.value;
                const meters = el.distance.value;
                const rawMinutes = seconds / 60;
                
                // Round to nearest 5 minutes for cleaner display
                const displayMinutes = Math.ceil(rawMinutes / 5) * 5;
                
                const result = {
                  minutes: displayMinutes,
                  kilometers: meters / 1000
                };
                
                // Stash in cache
                maps._etaCache = maps._etaCache || {};
                maps._etaCache[cacheKey] = { ts: Date.now(), value: result };
                
                resolve(result);
              } else {
                throw new Error(el?.status || 'UNKNOWN_ERROR');
              }
            }).catch(err => {
              attempt++;
              const shouldRetry = (err.message.includes('UNKNOWN_ERROR') || err.message === 'INVALID_REQUEST') && attempt < maxAttempts;
              if (shouldRetry) {
                // Retrying ETA request
                return resolve(run(200 * Math.pow(2, attempt))); // exponential back-off
              }
              
              // Set cooldown for quota errors
              if (err.message?.includes('OVER_QUERY_LIMIT')) {
                const cooldownUntil = Date.now() + (CONFIG.ETA_COOLDOWN_HOURS * 60 * 60 * 1000);
                sessionStorage.setItem('eta_cooldown_until', cooldownUntil.toString());
              }
              
              reject(err);
            });
          } catch (error) {
            logError('[SmokeysCart] Error creating service:', error);
            reject(error);
          }
        }, delay);
      });

      return run();
    }
  };

  // Apply rate limiting to the implementation
  maps.fetchLiveEta = utils.rateLimiter(maps.fetchLiveEtaImpl);

  // Delivery window functionality
  const delivery = {
    // Validate delivery window hasn't passed
    validateDeliveryWindow: (deliveryWindowStr) => {
      try {
        // Parse "Mon, Jul 28, 11:00 AM - 12:00 PM" format
        const match = deliveryWindowStr.match(/^(\w+), (\w+) (\d+), (\d{1,2}:\d{2} [AP]M) - (\d{1,2}:\d{2} [AP]M)$/);
        if (!match) {
          return { isValid: true }; // Can't parse, assume valid
        }

        const [, dayName, monthName, dayNum, startTime, endTime] = match;
        const currentYear = new Date().getFullYear();
        
        // Create date string and parse it
        const deliveryDateStr = `${monthName} ${dayNum}, ${currentYear} ${startTime}`;
        const deliveryStartDate = new Date(deliveryDateStr);
        
        // Get current time
        const now = new Date();
        
        // Check if delivery window has passed
        if (deliveryStartDate < now) {
          const minutesPast = Math.round((now - deliveryStartDate) / 60000);
          return {
            isValid: false,
            reason: `Delivery window has passed (${minutesPast} minutes ago)`,
            minutesOff: minutesPast
          };
        }
        
        // Check if too far in future (more than 3 days)
        const threeDaysFromNow = new Date(now);
        threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
        
        if (deliveryStartDate > threeDaysFromNow) {
          const daysInFuture = Math.round((deliveryStartDate - now) / (24 * 60 * 60 * 1000));
          return {
            isValid: false,
            reason: `Delivery window too far in future (${daysInFuture} days)`,
            minutesOff: daysInFuture * 24 * 60
          };
        }
        
        return { isValid: true };
      } catch (err) {
        logError('[SmokeysCart] Error validating delivery window:', err);
        return { isValid: true }; // Don't block on errors
      }
    },
    
    generateSlots: (store) => {
      if (!store || !store.isDeliveryAvailable) {
        return [];
      }
      
      const now = new Date();
      const earliestPossible = new Date(now);
      const cutoffMins = store.cutoffTime || 30;
      earliestPossible.setMinutes(earliestPossible.getMinutes() + cutoffMins);

      const slots = [];
      for (let dayOffset = 0; dayOffset < CONFIG.DAYS_TO_SHOW; dayOffset++) {
        const date = new Date(now);
        date.setDate(date.getDate() + dayOffset);

        const isToday = dayOffset === 0;
        const dateStr = isToday ? 'Today' : date.toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric'
        });

        const { start, end } = store.deliveryHours || { start: 11, end: 23 };
        const daySlots = [];

        // Fix for 24-hour edge case
        const lastWindowStartHour = end === 24 ? 23 : end - 1;

        // Use < instead of <= to avoid off-by-one
        for (let hour = start; hour < end && hour <= lastWindowStartHour; hour++) {
          const slotStart = new Date(date);
          slotStart.setHours(hour, 0, 0, 0);

          if (slotStart >= earliestPossible) {
            const slotEnd = new Date(slotStart);
            slotEnd.setHours(hour + 1);
            const startStr = slotStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            const endStr = slotEnd.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

            daySlots.push({
              id: `${date.toISOString().split('T')[0]}-${hour}`,
              date: dateStr,
              timeRange: `${startStr} - ${endStr}`
            });
          }
        }

        if (daySlots.length > 0) {
          slots.push({
            date: dateStr,
            dateObj: date,
            slots: daySlots
          });
        }
      }
      return slots;
    },

    saveSlot: async (slot) => {
      const formatted = `${slot.date}, ${slot.timeRange}`;
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Delivery Date': slot.date,
              'Delivery Time': slot.timeRange,
              'Delivery Window': formatted,
              '_delivery_slot_id': slot.id
            }
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to save slot: ${response.status}`);
        }
        
        localStorage.setItem('selectedDeliverySlot', JSON.stringify(slot));
        utils.eventBus.emit('delivery:slotSelected', slot);
        
        // Clear cart cache to ensure next read is fresh
        state.cartCache = null;
        state.cartCacheTime = 0;
        
        delivery.showConfirmation(formatted);
        
        // Show checkout button now that slot is selected
        const checkoutBtn = document.getElementById('checkout');
        const dynButtons = utils.qs('.cart__dynamic-checkout-buttons');
        if (checkoutBtn) checkoutBtn.style.display = '';
        if (dynButtons) dynButtons.style.display = '';
      } catch (err) {
        logError('[SmokeysCart] Error saving delivery slot:', err);
      }
    },

    showConfirmation: (formatted) => {
      const existing = utils.qs('.sc-delivery-confirmation');
      if (existing) existing.remove();
      
      const confirmation = document.createElement('div');
      confirmation.className = 'sc-delivery-confirmation';
      confirmation.textContent = `Delivery scheduled for ${formatted}`;
      
      const container = utils.qs('.sc-delivery-window-selector');
      if (container) {
        container.appendChild(confirmation);
      }
    },

    clearSlot: async () => {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Delivery Date': null,
              'Delivery Time': null,
              'Delivery Window': null,
              '_delivery_slot_id': null
            }
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to clear slot: ${response.status}`);
        }
        
        localStorage.removeItem('selectedDeliverySlot');
        utils.eventBus.emit('delivery:slotCleared');
        
        // Clear cart cache to ensure next read is fresh
        state.cartCache = null;
        state.cartCacheTime = 0;
        
        // Remove confirmation message
        const confirmation = utils.qs('.sc-delivery-confirmation');
        if (confirmation) confirmation.remove();
        
        // Hide checkout button
        const checkoutBtn = document.getElementById('checkout');
        const dynButtons = utils.qs('.cart__dynamic-checkout-buttons');
        if (checkoutBtn) checkoutBtn.style.display = 'none';
        if (dynButtons) dynButtons.style.display = 'none';
        
        // Delivery slot cleared
      } catch (err) {
        logError('[SmokeysCart] Error clearing delivery slot:', err);
      }
    },

    updateCheckoutVisibility: async () => {
      const checkoutBtn = document.getElementById('checkout');
      const dynButtons = utils.qs('.cart__dynamic-checkout-buttons');
      
      // Get cart to check attributes
      const cart = await utils.getCart();
      
      // Check if we're in delivery mode using localStorage
      const isDelivery = localStorage.getItem('deliverySelected') === 'true';
      if (!isDelivery) {
        // Not in delivery mode - always show checkout
        if (checkoutBtn) checkoutBtn.style.display = '';
        if (dynButtons) dynButtons.style.display = '';
        return;
      }
      
      // In delivery mode - check for actual slot selection
      const hasDeliverySlot = !!cart?.attributes?._delivery_slot_id;
      
      // VALIDATION: Check if delivery slot is still valid
      if (hasDeliverySlot && cart?.attributes?.['Delivery Window']) {
        const validationResult = delivery.validateDeliveryWindow(cart.attributes['Delivery Window']);
        if (!validationResult.isValid) {
          // Delivery slot has expired - clear it and notify user
          await delivery.clearSlot();
          
          // Show alert to user
          const alert = document.createElement('div');
          alert.className = 'cart__delivery-expired-alert';
          alert.innerHTML = `
            <div class="alert alert--warning">
              <strong>Delivery Time Expired</strong>
              <p>Your selected delivery window (${cart.attributes['Delivery Window']}) has passed. Please select a new delivery time.</p>
            </div>
          `;
          
          // Insert alert at top of cart
          const cartForm = utils.qs('.cart__contents');
          if (cartForm && !utils.qs('.cart__delivery-expired-alert')) {
            cartForm.insertBefore(alert, cartForm.firstChild);
          }
          
          // Scroll to delivery selector
          const deliverySelector = utils.qs('.sc-delivery-window-selector');
          if (deliverySelector) {
            deliverySelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          return; // Don't show checkout button
        }
      }
      
      // Check for express delivery variants
      const allExpressVariants = [];
      
      // Add default variants
      if (window.EXPRESS_VARIANTS) {
        Object.values(window.EXPRESS_VARIANTS).forEach(id => allExpressVariants.push(id));
      }
      
      // Add store-specific variants
      if (window.EXPRESS_VARIANTS_BY_STORE) {
        Object.values(window.EXPRESS_VARIANTS_BY_STORE).forEach(store => {
          Object.values(store).forEach(id => allExpressVariants.push(id));
        });
      }
      
      // Check if cart has any express delivery variant (handle both string and number types)
      const hasExpressDelivery = cart?.items?.some(item => 
        allExpressVariants.some(variantId => String(variantId) === String(item.variant_id))
      );
      
      const shouldShow = hasDeliverySlot || hasExpressDelivery;
      
      if (checkoutBtn) checkoutBtn.style.display = shouldShow ? '' : 'none';
      if (dynButtons) dynButtons.style.display = shouldShow ? '' : 'none';
    }
  };

  // Express delivery functionality
  const express = {
    // Store the latest ETA for availability calculations
    latestETA: null,
    
    // Cache for driver status
    driverStatusCache: null,
    driverStatusCacheTime: 0,
    
    // Get the next bulk slot start time (top of next hour)
    getNextBulkSlotStart: (now) => {
      const next = new Date(now);
      next.setHours(next.getHours() + 1, 0, 0, 0);
      return next;
    },
    
    // Simple availability - just check if within operating hours
    shouldShowExpress: () => {
      // Express is available whenever we're open
      // Driver manages their time within the 30-min windows
      return true;
    },
    
    isAvailable: (store) => {
      if (!store || !store.deliveryHours) return false;
      
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday
      const startHour = store.deliveryHours.start || 11;
      const endHour = store.deliveryHours.end || 23;
      
      const currentTimeInMinutes = currentHour * 60 + currentMinutes;
      
      // Express starts 45 min before first delivery window (10:15 AM)
      const expressStartTime = (startHour * 60) - 45;
      
      // Express ends at different times based on day of week
      // Friday (5) and Saturday (6): Express ends at 10:45 PM
      // Other days: Express ends at 9:45 PM
      let expressEndTime;
      if (dayOfWeek === 5 || dayOfWeek === 6) {
        // Friday/Saturday: 15 min before midnight (assuming last window is 11-12)
        expressEndTime = (endHour * 60) - 15; // 23:00 - 15 = 22:45 (10:45 PM)
      } else {
        // Sunday-Thursday: 15 min before last window starts
        expressEndTime = ((endHour - 1) * 60) - 15; // 22:00 - 15 = 21:45 (9:45 PM)
      }
      
      // Simple check - is current time within express hours?
      return currentTimeInMinutes >= expressStartTime && currentTimeInMinutes <= expressEndTime;
    },
    
    // Check if driver is on route (affects LIVE badge only)
    isDriverOnRoute: () => {
      // Check cache first
      const now = Date.now();
      if (express.driverStatusCache !== null && (now - express.driverStatusCacheTime) < 30000) {
        return express.driverStatusCache;
      }
      
      // First check the data attribute on the badge element
      const badge = document.querySelector(SEL.liveBadge);
      if (badge && badge.dataset.expressDisabled !== undefined) {
        // If express is disabled (false), driver is on route (Priority Delivery)
        const expressDisabled = badge.dataset.expressDisabled;
        // When metafield is false, Liquid outputs empty string or "false"
        // When metafield is true, Liquid outputs "true"
        const driverOnRoute = expressDisabled !== 'true';
        express.driverStatusCache = driverOnRoute;
        express.driverStatusCacheTime = now;
        return driverOnRoute;
      }
      
      // Fallback: check window object with new path
      if (window.shopMetafields?.express !== undefined) {
        // Check if express is an object with value property
        const expressValue = window.shopMetafields.express?.value !== undefined
          ? window.shopMetafields.express.value
          : window.shopMetafields.express;
        
        // If custom.express is false, driver is on route (Priority Delivery)
        const driverOnRoute = expressValue === false || expressValue === 'false';
        express.driverStatusCache = driverOnRoute;
        express.driverStatusCacheTime = now;
        return driverOnRoute;
      }
      
      // Default: show LIVE badge if we can't determine status
      express.driverStatusCache = false;
      express.driverStatusCacheTime = now;
      return false;
    },

    updateVisibility: () => {
      const selection = (typeof getCurrentStore === 'function') && getCurrentStore();
      const expressOption = document.getElementById(SEL.expressOpt.slice(1));
      const overlay = document.querySelector(SEL.overlay);
      
      if (!selection || !expressOption) return;
      
      if (selection.isDelivery) {
        expressOption.style.display = 'block';
        
        if (express.isAvailable(selection.store)) {
          // Express is available - hide overlay and show normal content
          if (overlay) overlay.style.display = 'none';
          express.updatePrice();
          express.addStickyBanner();
          // Don't call updateLiveBadge here - let autoUpdateETA handle it
          
          // Show countdown if before first window opens
          const now = new Date();
          const currentHour = now.getHours();
          const currentMinutes = now.getMinutes();
          const startHour = selection.store?.deliveryHours?.start || 11;
          const currentTimeInMinutes = currentHour * 60 + currentMinutes;
          const firstWindowTime = startHour * 60;
          
          if (currentTimeInMinutes < firstWindowTime) {
            express.startCutoffCountdown(selection.store);
          }
        } else {
          // Express is not available - show countdown overlay
          if (overlay) {
            overlay.style.display = 'flex';
            express.startUnavailableCountdown(selection.store);
          }
        }
      } else {
        expressOption.style.display = 'none';
      }
    },
    
    // Show why Express is valuable right now
    showValueComparison: (now) => {
      const comparison = document.getElementById('expressComparison');
      if (!comparison) return;
      
      const currentMinute = now.getMinutes();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0);
      
      if (currentMinute > 30) {
        // Missed cutoff
        const nextSlotTime = nextHour.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        comparison.textContent = `(Regular delivery: ${nextSlotTime} - ${new Date(nextHour.getTime() + 3600000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })})`;
        comparison.style.color = '#d35400'; // Orange to show urgency
      } else if (currentMinute <= 15) {
        // Just missed last delivery
        comparison.textContent = '(Just missed last delivery window!)';
        comparison.style.color = '#660eae';
      } else {
        // Normal comparison
        const regularTime = new Date(nextHour.getTime() + 1800000); // Middle of next window
        comparison.textContent = `(Regular delivery by ${regularTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })})`;
      }
    },
    
    // Add sticky banner for mobile
    addStickyBanner: () => {
      // Only on mobile and if not already added
      if (window.innerWidth > 768 || document.querySelector(SEL.stickyBanner)) return;
      
      const banner = document.createElement('div');
      banner.className = 'sc-express-sticky show';
      banner.innerHTML = `<button type="button">⚡ 30-Min Delivery Available</button>`;
      
      banner.querySelector('button').onclick = () => {
        const expressOption = document.getElementById('express-delivery-option');
        if (expressOption) {
          expressOption.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };
      
      document.body.appendChild(banner);
      
      // Hide on scroll
      let scrollTimeout;
      window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (window.scrollY > 600) {
            banner.classList.remove('show');
          }
        }, 100);
      }, { passive: true });
    },

    updatePrice: () => {
      const savedPosition = sessionStorage.getItem('userPosition');
      const selection = (typeof getCurrentStore === 'function') && getCurrentStore();
      
      if (!savedPosition || !selection || !selection.store) {
        return;
      }
      
      const userPos = utils.parsePosition(savedPosition);
      if (!userPos) return;
      
      try {
        const store = selection.store;
        const straightLineKm = utils.haversineKm(userPos.lat, userPos.lng, store.latitude, store.longitude);
        const km = straightLineKm * 1.5;
        
        // Get store-specific pricing
        const storeId = store.id || localStorage.getItem('selectedStoreId');
        const basePrice = window.getExpressPriceForDistance ? 
          window.getExpressPriceForDistance(km, storeId) :
          (km <= 10 ? 4.99 : km <= 20 ? 6.99 : km <= 30 ? 8.99 : 12.99);
        
        const priceEl = document.getElementById('express-delivery-price');
        if (priceEl) {
          priceEl.textContent = `(+$${basePrice})`;
        }
        
        // Don't update ETA here - let autoUpdateETA handle it properly
      } catch (e) {
        logError('[SmokeysCart] Error updating express delivery price:', e);
      }
    },
    
    // Update LIVE badge based on driver status
    updateLiveBadge: () => {
      const liveBadge = document.querySelector(SEL.liveBadge);
      const etaPill = document.querySelector(SEL.etaPill);
      
      if (!liveBadge) return;
      
      const isOnRoute = express.isDriverOnRoute();
      
      // Hide LIVE badge if driver is on route
      if (isOnRoute) {
        liveBadge.style.display = 'none';
        express.updatePriorityDeliveryDisplay();
        return;
      } else {
        liveBadge.style.display = '';
        // For live mode, ensure we have the proper structure with "mins"
        const etaValue = etaPill.querySelector('#eta-value');
        if (!etaValue) {
          etaPill.innerHTML = '<span id="eta-value">30</span>&nbsp;mins';
        }
      }
    },

    updateETA: async (userPos, store) => {
      // If driver is on route (Priority Delivery mode), show tier-based times
      if (express.isDriverOnRoute()) {
        const etaPill = document.querySelector('.eta-pill');
        if (etaPill) {
          const straightLineKm = utils.haversineKm(
            userPos.lat, userPos.lng,
            store.latitude, store.longitude
          );
          // Convert to driving distance
          const km = straightLineKm * 1.5;
          
          // Set ETA text based on distance tier
          if (km <= 20) {
            // Tier 1 & 2: 0-20km = "30 mins"
            etaPill.innerHTML = '<span id="eta-value">30</span>&nbsp;mins';
          } else {
            // Tier 3 & 4: 20-50km = "30-45 mins"
            etaPill.innerHTML = '<span id="eta-value">30-45</span>&nbsp;mins';
          }
        }
        return;
      }
      
      try {
        const result = await maps.fetchLiveEta(userPos, store);
        if (result) {
          // Ensure minimum 10 minutes first
          const minMinutes = Math.max(10, result.minutes);
          
          // Then apply buffer to the ETA
          const finalETA = Math.ceil(minMinutes * CONFIG.SAFETY_PADDING);
          
          // Store the buffered ETA for availability checks
          express.latestETA = finalETA;
          
          // Update the ETA value in the pill
          const etaValue = document.getElementById('eta-value');
          if (etaValue) {
            etaValue.textContent = finalETA;
          }
          
          // Update cart attribute with new ETA if express is selected
          const expressCheckbox = document.getElementById('express-delivery-checkbox');
          if (expressCheckbox && expressCheckbox.checked) {
            express.updateETAAttribute(finalETA);
          }
          
          // Update the live badge based on driver status
          express.updateLiveBadge();
          
          // Re-check availability with the new ETA
          express.updateVisibility();
        }
      } catch (err) {
        // Falling back to straight-line estimate
        
        // Fallback: haversine mins assuming 30 mph average speed
        try {
          const km = utils.haversineKm(userPos.lat, userPos.lng, store.latitude, store.longitude);
          const drivingKm = km * 1.5; // Convert straight-line to driving distance
          const baseMinutes = Math.round((drivingKm / 40) * 60); // 40 km/h average speed
          
          // Ensure minimum 10 minutes first
          const minMinutes = Math.max(10, baseMinutes);
          
          // Then apply safety buffer
          const finalETA = Math.ceil(minMinutes * CONFIG.SAFETY_PADDING);
          
          // Store the buffered ETA
          express.latestETA = finalETA;
          
          // Update the ETA value with estimate
          const etaValue = document.getElementById('eta-value');
          if (etaValue) {
            etaValue.textContent = `~${finalETA}`;
          }
          
          // Update cart attribute with new ETA if express is selected
          const expressCheckbox = document.getElementById('express-delivery-checkbox');
          if (expressCheckbox && expressCheckbox.checked) {
            express.updateETAAttribute(finalETA);
          }
          
          // Update the live badge based on driver status
          express.updateLiveBadge();
          
          // Re-check availability with the new ETA
          express.updateVisibility();
        } catch (fallbackErr) {
          // If even fallback fails, show default
          logError('[SmokeysCart] Fallback calculation failed:', fallbackErr);
          const etaValue = document.getElementById('eta-value');
          if (etaValue) {
            etaValue.textContent = '30';
          }
        }
      }
    },
    
    // Update ETA badge
    updateETABadge: (etaMins, isLive = false) => {
      // The new design has a static LIVE badge, so we don't need to update it
      // Keep this function for compatibility but it doesn't do anything now
    },
    
    // Update cart attribute with current ETA (throttled)
    updateETAAttribute: (() => {
      let lastUpdate = 0;
      let lastMinutes = 0;
      
      return async (minutes) => {
        const now = Date.now();
        // Only update if 30 seconds have passed AND the value changed by more than 2 minutes
        if (now - lastUpdate < 30000 || Math.abs(minutes - lastMinutes) < 3) {
          return;
        }
        
        lastUpdate = now;
        lastMinutes = minutes;
        
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: { 
                'Express Delivery ETA': `${minutes} minutes`
              }
            })
          });
        } catch (error) {
          logError('[SmokeysCart] Error updating ETA attribute:', error);
        }
      };
    })(),
    
    // Show cutoff countdown
    startCutoffCountdown: (store) => {
      if (!store?.deliveryHours) return;
      
      const now = new Date();
      const cutoff = new Date();
      const startHour = store.deliveryHours.start || 11;
      cutoff.setHours(startHour, 0, 0, 0);
      cutoff.setMinutes(cutoff.getMinutes() - 45); // 45-min buffer
      
      if (now > cutoff) return; // Already open
      
      const badge = document.getElementById('etaBadge');
      if (!badge) return;
      
      let tmr;
      function tick() {
        const diff = cutoff - new Date();
        if (diff <= 0) {
          badge.style.display = 'none';
          clearInterval(tmr);
          return;
        }
        const mm = Math.floor(diff / 60000).toString().padStart(2, '0');
        const ss = Math.floor(diff / 1000) % 60;
        badge.querySelector('.sc-eta-text').textContent = `Order in ${mm}:${ss.toString().padStart(2, '0')}`;
      }
      
      tick();
      tmr = setInterval(tick, 1000);
    },
    
    // Countdown for when express is unavailable
    startUnavailableCountdown: (store) => {
      if (!store?.deliveryHours) return;
      
      const updateCountdown = () => {
        const now = new Date();
        const tomorrow = new Date(now);
        
        // Calculate next available time (10:15 AM)
        const startHour = store.deliveryHours.start || 11;
        const expressStartTime = new Date(now);
        expressStartTime.setHours(startHour - 1, 15, 0, 0); // 45 minutes before first delivery
        
        // If we're past today's start time, move to tomorrow
        if (now >= expressStartTime) {
          expressStartTime.setDate(expressStartTime.getDate() + 1);
        }
        
        // Calculate time difference
        const diff = expressStartTime - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        // Update countdown display (no seconds for better performance)
        const hoursEl = document.getElementById('hours');
        const minutesEl = document.getElementById('minutes');
        const availableTimeEl = document.getElementById('available-time');
        
        if (hoursEl) hoursEl.textContent = hours.toString().padStart(2, '0');
        if (minutesEl) minutesEl.textContent = minutes.toString().padStart(2, '0');
        
        // Update available time
        if (availableTimeEl) {
          availableTimeEl.textContent = expressStartTime.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      };
      
      // Update immediately
      updateCountdown();
      
      // Clear any existing interval
      if (express.unavailableInterval) {
        clearInterval(express.unavailableInterval);
      }
      
      // Update every 5 seconds for better performance
      express.unavailableInterval = setInterval(updateCountdown, 5000);
    },

    // Update Priority Delivery fixed times
    updatePriorityDeliveryDisplay: () => {
      const etaPill = document.querySelector(SEL.etaPill);
      const liveBadge = document.querySelector(SEL.liveBadge);
      
      if (!etaPill) return;
      
      const savedPosition = sessionStorage.getItem('userPosition');
      const selection = (typeof getCurrentStore === 'function') && getCurrentStore();
      
      if (savedPosition && selection && selection.store) {
        const userPos = utils.parsePosition(savedPosition);
        if (userPos) {
          const straightLineKm = utils.haversineKm(
            userPos.lat, userPos.lng,
            selection.store.latitude, selection.store.longitude
          );
          const km = straightLineKm * 1.5;
          
          if (km <= 20) {
            etaPill.innerHTML = '<span id="eta-value">30</span>&nbsp;mins';
          } else {
            etaPill.innerHTML = '<span id="eta-value">30-45</span>&nbsp;mins';
          }
        }
      }
      
      // Ensure LIVE badge is hidden
      if (liveBadge) {
        liveBadge.style.display = 'none';
      }
    },
    
    // Combined refresh function
    refresh: () => {
      express.updatePrice();
      express.autoUpdateETA();
      express.updateLiveBadge();
    },
    
    autoUpdateETA: () => {
      // If in Priority Delivery mode, update the fixed display
      if (express.isDriverOnRoute()) {
        express.updatePriorityDeliveryDisplay();
        return;
      }
      
      const expressOption = document.getElementById('express-delivery-option');
      
      if (!expressOption || expressOption.style.display === 'none') {
        return;
      }
      
      const pos = sessionStorage.getItem('userPosition');
      const selection = (typeof getCurrentStore === 'function') && getCurrentStore();
      
      if (!pos || !selection || !selection.store) {
        return;
      }
      
      const userPos = utils.parsePosition(pos);
      if (userPos) {
        express.updateETA(userPos, selection.store);
      }
    }
  };

  // Initialize the system
  const init = () => {
    if (state.initialized) return;
    
    // Initializing...
    
    // Check delivery slot validity on page load
    const checkDeliverySlotOnLoad = async () => {
      const cart = await utils.getCart();
      if (cart?.attributes?.['Delivery Window']) {
        const validation = delivery.validateDeliveryWindow(cart.attributes['Delivery Window']);
        if (!validation.isValid) {
          // Clear expired slot and force checkout visibility update
          await delivery.clearSlot();
          delivery.updateCheckoutVisibility();
        }
      }
    };
    
    // Run validation after a short delay to ensure DOM is ready
    setTimeout(checkDeliverySlotOnLoad, 100);
    
    // Add styles for delivery alerts
    if (!document.getElementById('smokeys-cart-delivery-alert-styles')) {
      const alertStyles = document.createElement('style');
      alertStyles.id = 'smokeys-cart-delivery-alert-styles';
      alertStyles.textContent = `
        .cart__delivery-expired-alert {
          margin-bottom: 20px;
        }
        
        .cart__delivery-expired-alert .alert {
          background-color: #fff3cd;
          border: 1px solid #ffeeba;
          color: #856404;
          padding: 15px 20px;
          border-radius: 8px;
          margin: 0;
        }
        
        .cart__delivery-expired-alert .alert strong {
          display: block;
          margin-bottom: 5px;
          font-size: 1.1em;
        }
        
        .cart__delivery-expired-alert .alert p {
          margin: 0;
          font-size: 0.95em;
        }
      `;
      document.head.appendChild(alertStyles);
    }
    
    // Set up event listeners
    utils.eventBus.on('cart:updated', (e) => {
      express.updateVisibility();
      delivery.updateCheckoutVisibility();
    });
    
    window.addEventListener('userPositionChanged', async () => {
      // Clear express delivery and time slots when address changes
      const cart = await utils.getCart();
      const hasExpressDelivery = cart?.items?.some(item => item.handle === 'express-delivery');
      const hasDeliverySlot = !!cart?.attributes?._delivery_slot_id;
      
      // Check if current store still supports delivery at new location
      const storeInfo = (typeof getCurrentStore === 'function') && getCurrentStore();
      const isDeliveryMode = localStorage.getItem('deliverySelected') === 'true';
      
      if (isDeliveryMode && storeInfo && storeInfo.store) {
        // Check if delivery is still available at new location
        const userPos = sessionStorage.getItem('userPosition');
        if (userPos) {
          const pos = JSON.parse(userPos);
          const distanceKm = utils.haversineKm(
            pos.lat, pos.lng,
            storeInfo.store.latitude, storeInfo.store.longitude
          );
          
          // Update Express delivery price and ETA for new location
          express.updatePrice();
          express.autoUpdateETA();
          
          // Check if out of delivery range for ANY store (including virtual)
          if (storeInfo.store.deliveryRadius && distanceKm > storeInfo.store.deliveryRadius) {
            
            // Clear any delivery selections from cart
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: {
                  'Delivery Date': null,
                  'Delivery Time': null,
                  'Delivery Window': null,
                  '_delivery_slot_id': null,
                  'Express Delivery': null,
                  'Express Delivery ETA': null,
                  'Express Delivery Price': null
                }
              })
            });
            
            // Show branded modal
            const modal = document.createElement('div');
            modal.className = 'smokeys-alert-modal';
            modal.innerHTML = `
              <div class="smokeys-alert-overlay"></div>
              <div class="smokeys-alert-content">
                <div class="smokeys-alert-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#660eae" stroke-width="2">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                  </svg>
                </div>
                <h3 class="smokeys-alert-title">Outside Delivery Area</h3>
                <p class="smokeys-alert-message">
                  Your new address is outside of the delivery radius of the nearest Smokey's Delivery store.
                  <br><br>
                  Please select a pickup location or enter a different delivery address.
                </p>
                <button class="smokeys-alert-button" onclick="this.closest('.smokeys-alert-modal').remove(); if(typeof openStoreLocator === 'function') openStoreLocator();">
                  Find your Smokey's
                </button>
              </div>
            `;
            
            // Add styles
            const style = document.createElement('style');
            style.textContent = `
              .smokeys-alert-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 99999;
                display: flex;
                align-items: center;
                justify-content: center;
                animation: fadeIn 0.3s ease;
              }
              
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              
              .smokeys-alert-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.6);
              }
              
              .smokeys-alert-content {
                position: relative;
                background: white;
                border-radius: 16px;
                padding: 32px;
                max-width: 420px;
                width: 90%;
                text-align: center;
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
                animation: slideUp 0.3s ease;
              }
              
              @keyframes slideUp {
                from { transform: translateY(20px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
              
              .smokeys-alert-icon {
                margin-bottom: 20px;
              }
              
              .smokeys-alert-title {
                color: #660eae;
                font-size: 1.5rem;
                font-weight: 700;
                margin: 0 0 16px 0;
                font-family: inherit;
              }
              
              .smokeys-alert-message {
                color: #000;
                font-size: 1.1rem;
                line-height: 1.5;
                margin: 0 0 24px 0;
              }
              
              .smokeys-alert-button {
                background: linear-gradient(135deg, #660eae 0%, #8b3dc3 100%);
                color: white;
                border: none;
                border-radius: 25px;
                padding: 12px 32px;
                font-size: 1rem;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
              }
              
              .smokeys-alert-button:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(102, 14, 174, 0.3);
              }
              
              @media (max-width: 480px) {
                .smokeys-alert-content {
                  padding: 24px;
                }
                
                .smokeys-alert-title {
                  font-size: 1.25rem;
                }
                
                .smokeys-alert-message {
                  font-size: 0.9rem;
                }
              }
            `;
            
            document.head.appendChild(style);
            document.body.appendChild(modal);
            
            return; // Exit early
          }
        }
      }
      
      // Get all possible express delivery variant IDs
      const allExpressVariants = [];
      
      // Add default variants
      if (window.EXPRESS_VARIANTS) {
        Object.values(window.EXPRESS_VARIANTS).forEach(id => allExpressVariants.push(id));
      }
      
      // Add store-specific variants
      if (window.EXPRESS_VARIANTS_BY_STORE) {
        Object.values(window.EXPRESS_VARIANTS_BY_STORE).forEach(store => {
          Object.values(store).forEach(id => allExpressVariants.push(id));
        });
      }
      
      // Check if cart has any express delivery variants
      const expressItemsInCart = cart?.items?.filter(item => 
        allExpressVariants.includes(item.variant_id)
      ) || [];
      
      if (expressItemsInCart.length > 0 || hasDeliverySlot) {
        // Address changed, clearing delivery selections
        
        try {
          // Build update payload
          const updatePayload = {
            attributes: {}
          };
          
          // Clear delivery slot if exists
          if (hasDeliverySlot) {
            updatePayload.attributes['Delivery Date'] = null;
            updatePayload.attributes['Delivery Time'] = null;
            updatePayload.attributes['Delivery Window'] = null;
            updatePayload.attributes['_delivery_slot_id'] = null;
          }
          
          // Clear express delivery variants if exist
          if (expressItemsInCart.length > 0) {
            updatePayload.updates = {};
            expressItemsInCart.forEach(item => {
              updatePayload.updates[item.variant_id] = 0;
            });
            updatePayload.attributes['Express Delivery'] = null;
          }
          
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload)
          });
          
          // Clear cart cache
          state.cartCache = null;
          state.cartCacheTime = 0;
          
          // Clear local storage
          localStorage.removeItem('selectedDeliverySlot');
          
          // Update UI
          const checkbox = document.getElementById('express-delivery-checkbox');
          const container = document.querySelector('.express-card');
          if (checkbox) checkbox.checked = false;
          if (container) container.classList.remove('selected');
          
          // Reload to update cart display
          window.location.reload();
        } catch (error) {
          logError('[SmokeysCart] Error clearing delivery selections:', error);
        }
      } else {
        // Just update price if no selections to clear
        express.updatePrice();
        // Update Priority Delivery display if needed
        if (express.isDriverOnRoute()) {
          express.updatePriorityDeliveryDisplay();
        }
      }
    });
    
    window.addEventListener('storeSelected', () => {
      express.updateVisibility();
      express.updatePrice();
      // Update Priority Delivery times if needed
      if (express.isDriverOnRoute()) {
        express.updatePriorityDeliveryDisplay();
      }
    });
    
    // Watch for express delivery visibility changes
    const watchExpressVisibility = () => {
      const expressOption = document.getElementById('express-delivery-option');
      if (expressOption) {
        const observer = new MutationObserver(() => {
          if (expressOption.style.display !== 'none') {
            express.autoUpdateETA();
          }
        });
        observer.observe(expressOption, { attributes: true, attributeFilter: ['style'] });
        state.observers.push(observer);
      }
    };
    
    watchExpressVisibility();
    
    // Periodic updates
    const intervalId = setInterval(() => {
      express.updateVisibility();
      express.autoUpdateETA(); // Also update ETA periodically
    }, 60000);
    state.intervals.push(intervalId);
    
    // Clean up on section unload
    document.addEventListener('shopify:section:unload', (e) => {
      if (e.detail.sectionId === 'main-cart-footer') {
        // Cleaning up...
        utils.cleanup();
        state.initialized = false;
        window.SmokeysCart.initialized = false;
      }
    });
    
    // Re-attach observer on section reload
    document.addEventListener('shopify:section:load', (e) => {
      if (e.detail.sectionId === 'main-cart-footer') {
        // Section reloaded, re-initializing...
        watchExpressVisibility();
        express.updateVisibility();
      }
    });
    
    // Clean up intervals on page unload
    window.addEventListener('beforeunload', () => {
      if (express.unavailableInterval) {
        clearInterval(express.unavailableInterval);
      }
      // Clear any other intervals
      state.intervals.forEach(interval => clearInterval(interval));
    });
    
    state.initialized = true;
    window.SmokeysCart.initialized = true;
    // Initialization complete
  };

  // Public API
  window.SmokeysCart = {
    ...window.SmokeysCart,
    init,
    delivery,
    express,
    utils,
    maps,
    cleanup: utils.cleanup,
    initialized: false,
    
    // Export commonly used functions
    haversineKm: utils.haversineKm,
    fetchLiveEta: maps.fetchLiveEta
  };

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Remove temporary workaround - metafield logic now handles LIVE badge visibility
})();